import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * VesselMind Context
 * 
 * Isolated state management for the VesselMind module.
 * No dependencies on main app state or context.
 */
const VesselMindContext = createContext(null);

export const VesselMindProvider = ({ children }) => {
  // Chat state
  const WELCOME_MESSAGE = 'Welcome to VesselMind — regulatory decision support for LPG Codes of Practice.\n\nI structure guidance from across the full library of Codes of Practice, identifying applicable clauses, surfacing conditions and exceptions, and displaying exact source text.\n\nTry asking questions like:\n- "What are the separation distance requirements for bulk LPG storage?"\n- "What materials are referenced for LPG pipework?"\n- "What clauses cover pressure relief valve requirements?"\n\nVesselMind does not make compliance determinations. All outputs require professional validation by a competent person before application. This tool provides traceable regulatory awareness — not certification.';

  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: WELCOME_MESSAGE,
      timestamp: new Date().toISOString()
    }
  ]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [pdfDocuments, setPdfDocuments] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasAskedClarifyingQuestion, setHasAskedClarifyingQuestion] = useState(false);
  
  // Admin status and user role (check from localStorage)
  const [isAdmin, setIsAdmin] = useState(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user.isAdmin === true;
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
    return false;
  });

  const [userRole] = useState(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        return JSON.parse(userData).role || 'standard';
      }
    } catch (error) { /* ignore */ }
    return 'standard';
  });

  // Add a new message
  const addMessage = useCallback((role, content) => {
    const newMessage = {
      id: Date.now().toString(),
      role,
      content,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  }, []);

  // Clear chat history
  const clearChat = useCallback(() => {
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: WELCOME_MESSAGE,
        timestamp: new Date().toISOString()
      }
    ]);
    setHasAskedClarifyingQuestion(false);
  }, []);

  // Initialize PDF documents
  const initializePdfDocuments = useCallback((docs) => {
    setPdfDocuments(docs);
    setIsInitialized(true);
  }, []);

  const value = {
    // State
    messages,
    isLoading,
    pdfDocuments,
    isInitialized,
    hasAskedClarifyingQuestion,
    isAdmin,
    userRole,
    
    // Actions
    addMessage,
    clearChat,
    setIsLoading,
    initializePdfDocuments,
    setHasAskedClarifyingQuestion,
    setIsAdmin
  };

  return (
    <VesselMindContext.Provider value={value}>
      {children}
    </VesselMindContext.Provider>
  );
};

// Custom hook to use VesselMind context
export const useVesselMind = () => {
  const context = useContext(VesselMindContext);
  if (!context) {
    throw new Error('useVesselMind must be used within VesselMindProvider');
  }
  return context;
};

